/* eslint-disable unused-imports/no-unused-vars */
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export interface SharedCacheOptions {
    /**
     * The expiration time by milliseconds.
     */
    expiration?: number;

    /**
     * Flag to force the cache to refresh.
     */
    forceRefresh?: boolean;
}

export interface SharedCacheContainer<TClass> {
    /**
     * The last timestamp for the instance collected.
     */
    timestamp: number;

    /**
     * The instance of the data class.
     */
    instance: TClass;
}

interface SharedCacheCollection<TClass> {
    [instanceId: string]: SharedCacheContainer<TClass>;
}

/**
 * Abstract class of SharedCache by using session storage.
 *
 * TClass: the caching data class type.
 * TData: the caching data interface type.
 * TParams: the data query parameters type.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export abstract class SharedCache<TClass, TData, TParams> {
    private cache: SharedCacheCollection<TClass> = {};

    /**
     * Initializes a new instance of the SharedCache class.
     *
     * @param uniqueId the unique identity of the collection of data.
     * @param uniqueVersion the unique version of data. (Should increment any data format or query change)
     * @param instanceId the instance id generator.
     * @param serialize the data serialization.
     * @param deserialize the data deserialization.
     * @param getDataQuery the query observable.
     * @param options the shared cache options.
     */
    constructor(
        private uniqueId: string,
        private uniqueVersion: number,
        private instanceId: (params: TParams) => string,
        private serialize: (instance: TClass) => string,
        private deserialize: (serialized: string) => TClass,
        private getDataQuery?: (params: TParams) => Observable<TClass>,
        private options?: SharedCacheOptions) { }

    /**
     * Gets the id of the collection of data.
     */
    public get id(): string {
        return this.uniqueId;
    }

    /**
     * Gets the version of the cache for format and query.
     */
    public get version(): number {
        return this.uniqueVersion;
    }

    /**
     * Check if the Cache has unexpired data available for the instance id.
     * @param params the parameter to check for the data.
     */
    public isAvailable(params: TParams): boolean {
        const instanceId = this.instanceId(params);
        if (this.cache[instanceId] && !this.expired(this.cache[instanceId])) {
            return true;
        }

        if (MsftSme.SessionStorageHandler.getSessionStorage()?.[this.cacheKey]) {
            this.sessionRestore();
            if (this.cache[instanceId] && !this.expired(this.cache[instanceId])) {
                return true;
            }
        }

        return false;
    }

    /**
     * Query to find the data.
     * @param params the parameter to query the data.
     */
    public query(params: TParams): Observable<SharedCacheContainer<TClass>> {
        if (this.isAvailable(params)) {
            const instanceId = this.instanceId(params);
            return of(this.cache[instanceId]);
        }

        return this.update(params);
    }

    /**
     * Refresh the data.
     * @param params the parameter to query the data.
     */
    public refresh(params: TParams): Observable<SharedCacheContainer<TClass>> {
        if (MsftSme.SessionStorageHandler.getSessionStorage()?.[this.cacheKey]) {
            this.sessionRestore();
        }

        return this.update(params);
    }

    /**
     * Save an instance data to session storage.
     *
     * @param params the parameter to query the data.
     * @param data the data object to store.
     */
    public save(params: TParams, data: TClass): void {
        if (MsftSme.SessionStorageHandler.getSessionStorage()?.[this.cacheKey]) {
            this.sessionRestore();
        }

        this.sessionSave(params, data);
    }

    /**
     * Clear the cache.
     *
     * @param params the parameter to query the data (optional to delete an instance)
     */
    public clear(params?: TParams): void {
        if (params) {
            if (MsftSme.SessionStorageHandler.getSessionStorage()?.[this.cacheKey]) {
                this.sessionRestore();
            }

            this.sessionSave(params);
        } else {
            this.cache = {};
            if (MsftSme.SessionStorageHandler.getSessionStorage()) {
                MsftSme.SessionStorageHandler.getSessionStorage()[this.cacheKey] = JSON.stringify(this.cache);
            }
        }
    }

    /**
     * Force restore whole data and return the data for the params specified.
     *
     * @param params the parameter to query the data.
     * @return SharedCacheContainer<TClass> the data for the params specified.
     */
    public forceRestore(params: TParams): SharedCacheContainer<TClass> {
        if (MsftSme.SessionStorageHandler.getSessionStorage()?.[this.cacheKey]) {
            const instanceId = this.instanceId(params);
            this.sessionRestore();
            return this.cache[instanceId];
        }

        return null;
    }

    private expired(data: SharedCacheContainer<TClass>): boolean {
        if (this.options && this.options.forceRefresh) {
            return true;
        }

        if (!this.options || !this.options.expiration) {
            return false;
        }

        return (Date.now() - data.timestamp) > this.options.expiration;
    }

    private update(params: TParams): Observable<SharedCacheContainer<TClass>> {
        if (this.getDataQuery) {
            const instanceId = this.instanceId(params);
            return this.getDataQuery(params)
                .pipe(
                    map(data => {
                        this.sessionSave(params, data);
                        return this.cache[instanceId];
                    }));
        } else {
            return of(null);
        }
    }

    private sessionRestore(): void {
        const preProcessed: SharedCacheCollection<string> = JSON.parse(sessionStorage[this.cacheKey]);
        const cache: SharedCacheCollection<TClass> = {};
        const keys = Object.keys(preProcessed);
        keys.forEach(key => {
            cache[key] = <SharedCacheContainer<TClass>>{
                timestamp: preProcessed[key].timestamp,
                instance: this.deserialize(preProcessed[key].instance)
            };
        });
        this.cache = cache;
    }

    private sessionSave(params: TParams, data?: TClass): void {
        const instanceId = this.instanceId(params);
        if (data) {
            this.cache[instanceId] = { timestamp: Date.now(), instance: data };
        } else {
            delete this.cache[instanceId];
        }

        const preProcessed: SharedCacheCollection<string> = {};
        const keys = Object.keys(this.cache);
        keys.forEach(key => {
            preProcessed[key] = <SharedCacheContainer<string>>{
                timestamp: this.cache[key].timestamp,
                instance: this.serialize(this.cache[key].instance)
            };
        });

        if (MsftSme.SessionStorageHandler.getSessionStorage()) {
            MsftSme.SessionStorageHandler.getSessionStorage()[this.cacheKey] = JSON.stringify(preProcessed);
        }
    }

    private get cacheKey(): string {
        // used as sessionStorage key name.
        return 'shared-cache:{0}.{1}'.format(this.id, this.version);
    }
}
